import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { borrowerActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { ValidationMessagesModal, StateSelect, ZipCodeInput, BfiCustomStore } from '../../common/SharedComponents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { BorrowerApi } from '@apply/api';
import { YearInput } from '@apply/ui';
import { applicationInsights } from '../../ApplicationInsights';
import { EntityPrincipalLinks, CompanySelector } from '../common/BorrowerComponents';
import { RefreshEvents } from '../../common/SharedEvents';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { TabPanel, Item } from 'devextreme-react/tab-panel';
import EntityApplicationsTable from '../tables/EntityApplicationsTable';
import EntityLoansTable from '../tables/EntityLoansTable';
import { EntityConcernType, EntityConcernTypeOptions, EntityResponsivenessTypeOptions } from '../common/BorrowerConstants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const EditEntityModal = () => {
    const { isOpen = false, entityId, onChange, parentCompanyId = null } = useSelector((state) => state.borrower.editEntityModal);
    const { dispatch } = store;
    const borrowerApi = new BorrowerApi.BorrowerApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const applicationsStore = new BfiCustomStore();
    const masterNotesStore = new BfiCustomStore();
    const loansStore = new BfiCustomStore();

    const tabNames = ['Details', 'Principals', 'Applications', 'Master Notes', 'Loans'];
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);

    const [entity, set_entity] = useState(null);
    const [companyId, set_companyId] = useState(0);
    const [name, set_name] = useState('');
    const [addressSameAsCompany, set_addressSameAsCompany] = useState(false);
    const [street1, set_street1] = useState('');
    const [street2, set_street2] = useState('');
    const [city, set_city] = useState('');
    const [state, set_state] = useState('');
    const [zipCode, set_zipcode] = useState('');
    const [tin, set_tin] = useState('');
    const [entityType, set_entityType] = useState(0);
    const [stateIncorporated, set_stateIncorporated] = useState('');
    const [yearIncorporated, set_yearIncorporated] = useState('');
    const [concernTypeId, set_concernTypeId] = useState('');
    const [concernReason, set_concernReason] = useState('');
    const [responsivenessTypeId, set_responsivenessTypeId] = useState('');
    const [workingRelationship, set_workingRelationship] = useState('');

    //Dialogs
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');
    const [validation, set_validation] = useState(undefined);

    const handleClose = () =>
        dispatch(
            borrowerActions.setEditEntityModal({
                editEntityModal: { isOpen: false },
            })
        );

    const styles = {
        buttonContainer: {
            marginRight: '10px',
            marginLeft: '10px',
            textAlign: 'right',
        },
        buttonMarginRight: { marginRight: '10px' },
        sectionHeader: { margin: '10px 0' },
        formColumn: { paddingBottom: '5px', paddingRight: '5px' },
        loansParentPanel: { width: '100%', minHeight: '700px', padding: '10px' },
    };

    useEffect(() => {
        async function entityIdChanged() {
            if (!entityId) {
                setState({ companyId: parentCompanyId });
                set_loadingOpen(false);
                return;
            }
            const response = await borrowerApi.getEntityById(entityId);
            if (response.statusCode !== 200) return;
            const entity = response.result;
            setState(entity);

            set_loadingOpen(false);
        }

        entityIdChanged();
    }, [entityId, parentCompanyId]);

    const validateEntity = async () => {
        set_loadingMessage('Validating...');
        try {
            const model = {
                entityId,
                name,
                tin,
                state: stateIncorporated,
            };

            const response = await borrowerApi.validateEntity(model);
            if (response.statusCode !== 200 || !response.result) {
                set_loadingOpen(false);
                return false;
            }

            if (response.result.hasErrors || response.result.hasWarnings) {
                set_validation(response.result);
                set_loadingOpen(false);
                return false;
            }

            return true;
        } catch (ex) {
            set_loadingOpen(false);
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
            return false;
        }
    };

    const validateAndSave = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        var isOk = await validateEntity();
        if (!isOk) {
            return;
        }

        await saveEntity();
    };

    const saveEntity = async () => {
        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                entityId,
                companyId,
                name,
                addressSameAsCompany,
                street1,
                street2,
                city,
                state,
                zipCode,
                tin,
                entityType,
                stateIncorporated,
                yearIncorporated,
                concernTypeId,
                concernReason,
                responsivenessTypeId,
                workingRelationship,
            };

            set_loadingMessage('Saving changes...');
            const response = await borrowerApi.saveEntity(model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            if (response.result.hasErrors || response.result.hasWarnings) {
                set_validation(response.result);
                set_loadingOpen(false);
                return false;
            }

            refreshEvents.dispatchRefreshEvent();

            const savedEntity = response.result;
            setState(savedEntity);
            onChange && onChange(savedEntity);

            handleAdminSuccess('Borrower Entity Saved successfully.');
            set_loadingOpen(false);

            setTimeout(() => {
                handleClose();
            }, 600);
        } catch (ex) {
            set_loadingOpen(false);
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        }
    };

    const onValidationConfirm = async () => {
        set_validation(undefined);
        await saveEntity();
    };

    const onValidationCancel = () => {
        set_validation(undefined);
    };

    const resetState = () => {
        set_companyId(0);
        set_name('');
        set_tin('');
        set_entityType(0);
        set_stateIncorporated('');
        set_yearIncorporated('');
        set_addressSameAsCompany(false);
        set_street1('');
        set_street2('');
        set_city('');
        set_state('');
        set_zipcode('');
        set_validation(undefined);
        set_concernTypeId('');
        set_concernReason('');
        set_responsivenessTypeId('');
        set_workingRelationship('');
    };

    const formatEin = (ein) => {
        return !ein
            ? ''
            : ein.length > 11
              ? ein.substr(0, 11)
              : ein
                    .replace(/[^\d*]/g, '')
                    //.replace(/(?=\d{5})\d/, '*')
                    .replace(/^(.{1,2})(.{1,7})?.*$/, (_, x, y, z) => x + (y ? `-${y}` : '') + (z ? `-${z}` : ''));
    };

    const onYearSelection = (value) => {
        if (value && value !== '' && value != null) {
            let selectedDate = new Date(value).toISOString().split('T')[0];
            set_yearIncorporated(selectedDate);
        } else {
            set_yearIncorporated(null);
        }
    };

    const setState = (ent) => {
        if (ent) {
            set_entity(ent);
            set_companyId(ent.companyId);
            set_name(ent.name);
            set_tin(ent.tin);
            set_entityType(ent.entityType);
            set_stateIncorporated(ent.stateIncorporated);
            set_yearIncorporated(ent.yearIncorporated);
            set_addressSameAsCompany(ent.addressSameAsCompany);
            set_street1(ent.street1);
            set_street2(ent.street2);
            set_city(ent.city);
            set_state(ent.state);
            set_zipcode(ent.zipCode);
            set_concernTypeId(ent.concernTypeId ?? '');
            set_concernReason('');
            set_responsivenessTypeId(ent.responsivenessTypeId ?? '');
            set_workingRelationship(ent.workingRelationship ?? '');
        } else {
            resetState();
        }
    };

    const onCompanySelect = (company) => {
        set_companyId(company?.companyId);
    };

    const onSameAsCompanyAddressChanged = async (sameAsCompany) => {
        if (!companyId) {
            set_addressSameAsCompany(false);
            return;
        }

        if (sameAsCompany) {
            //Retrieve parent company address
            const response = await borrowerApi.getCompanyById(companyId);
            if (response.statusCode !== 200) {
                return;
            }
            const company = response.result;
            set_street1(company.street1);
            set_street2(company.street2);
            set_city(company.city);
            set_state(company.state);
            set_zipcode(company.zipCode);
        }
        set_addressSameAsCompany(sameAsCompany);
    };

    const onTabChanged = (e) => {
        setSelectedTabIndex(tabNames.indexOf(e.addedItems[0].title));
    };

    const getConcernStyle = (typeId) => {
        if (!typeId) {
            return null;
        }

        switch (parseInt(typeId)) {
            case EntityConcernType.Green:
                return { backgroundColor: '#86c786' };
            case EntityConcernType.Yellow:
                return { backgroundColor: '#e2e273' };
            case EntityConcernType.Red:
                return { backgroundColor: '#f86363' };
            default:
                return null;
        }
    };

    const getWatchlistStatus = (entity) => {
        if (!entity) {
            return null;
        }
        if (entity.onWatchlist) {
            return (
                <span>
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        style={{ color: 'darkred' }}
                        title='This Entity is has loans on the Current (or Draft) watchlists'
                        size='lg'
                    ></FontAwesomeIcon>
                    &nbsp;
                    <span>Borrower is on Watchlist</span>
                </span>
            );
        } else {
            return (
                <span>
                    <FontAwesomeIcon
                        icon={faCheckCircle}
                        style={{ color: 'green' }}
                        title='This Entity has no loans on the Current (or Draft) watchlists'
                        size='lg'
                    ></FontAwesomeIcon>
                    &nbsp;
                    <span>Borrower is not on Watchlist</span>
                </span>
            );
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='lg'
                open={isOpen}
                onClose={handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '1100px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form
                                    name='editBorrowerForm'
                                    onSubmit={(event) => validateAndSave(event)}
                                >
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1
                                            style={{
                                                height: '50px',
                                                marginTop: '15px',
                                            }}
                                        >
                                            {entityId ? 'Edit Borrower Entity' : 'Add Borrower Entity'}
                                        </h1>
                                        <button
                                            type='button'
                                            className='close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={handleClose}
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>
                                    <div
                                        className='scrollable-content'
                                        style={{ margin: '10px' }}
                                    >
                                        <TabPanel onSelectionChanged={onTabChanged}>
                                            <Item
                                                title={tabNames[0]}
                                                icon='home'
                                            >
                                                <div
                                                    className=''
                                                    style={{
                                                        padding: '0 10px 0 10px',
                                                    }}
                                                >
                                                    <h2
                                                        className='decorated'
                                                        style={styles.sectionHeader}
                                                    >
                                                        General
                                                    </h2>
                                                    <div className='row'>
                                                        <div
                                                            className='columns small-12 large-10'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='name'>
                                                                Entity Name <span className='req'>*</span>
                                                            </label>
                                                            <input
                                                                type='text'
                                                                id='name'
                                                                value={name}
                                                                selected={name}
                                                                onChange={(e) => set_name(e.target.value)}
                                                                required={true}
                                                                maxLength={200}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className='row'>
                                                        <div
                                                            className='columns small-12 large-3'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='tin'>TIN #</label>
                                                            <input
                                                                type='text'
                                                                maxLength={11}
                                                                id='tin'
                                                                value={tin}
                                                                onChange={(e) => set_tin(formatEin(e.target.value))}
                                                                required={false}
                                                            />
                                                        </div>
                                                        <div
                                                            className='columns small-12 large-4'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='entityType'>
                                                                Entity Type <span className='req'>*</span>
                                                            </label>
                                                            <select
                                                                id='entityType'
                                                                value={entityType}
                                                                onChange={(e) => set_entityType(parseInt(e.target.value))}
                                                                required={true}
                                                            >
                                                                <option value='0'>Limited Liability Company (LLC)</option>
                                                                <option value='1'>Partnership</option>
                                                                <option value='2'>S Corporation</option>
                                                                <option value='3'>C Corporation</option>
                                                            </select>
                                                        </div>
                                                        <div
                                                            className='columns small-12 medium-6 large-3'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='stateIncorporated'>
                                                                State Incorporated <span className='req'>*</span>
                                                            </label>
                                                            <StateSelect
                                                                id='stateIncorporated'
                                                                value={stateIncorporated}
                                                                onChange={(event) => set_stateIncorporated(event.target.value)}
                                                                required={true}
                                                            />
                                                        </div>
                                                        <div
                                                            className='columns small-12 medium-6 large-2'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='yearIncorporated'>Year Incorporated</label>
                                                            <YearInput
                                                                id='yearIncorporated'
                                                                value={
                                                                    yearIncorporated
                                                                        ? new Date(yearIncorporated).getUTCFullYear()
                                                                        : null
                                                                }
                                                                onChange={(event) => onYearSelection(event.target.value)}
                                                                maxWidth='190px'
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className='row'>
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={styles.formColumn}
                                                        >
                                                            <CompanySelector
                                                                companyId={companyId}
                                                                required={true}
                                                                title='Business (Marketing Name)'
                                                                onSelect={onCompanySelect}
                                                            />
                                                        </div>
                                                    </div>

                                                    <h2
                                                        className='decorated'
                                                        style={styles.sectionHeader}
                                                    >
                                                        Watchlist
                                                    </h2>
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-4'
                                                            style={styles.formColumn}
                                                        >
                                                            {getWatchlistStatus(entity)}
                                                        </div>
                                                    </div>
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-4'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='concernTypeId'>Concern:</label>
                                                            <select
                                                                id='concernTypeId'
                                                                value={concernTypeId}
                                                                required={false}
                                                                onChange={(e) => set_concernTypeId(e.target.value)}
                                                                style={getConcernStyle(concernTypeId)}
                                                            >
                                                                <option value=''>Select...</option>
                                                                {EntityConcernTypeOptions.map((r) => {
                                                                    return (
                                                                        <option
                                                                            key={r.id}
                                                                            value={r.id}
                                                                            style={getConcernStyle(r.id)}
                                                                        >
                                                                            {r.name}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </select>
                                                        </div>
                                                        <div
                                                            className='columns small-12 large-8'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='responsivenessTypeId'>Responsiveness:</label>
                                                            <select
                                                                id='responsivenessTypeId'
                                                                value={responsivenessTypeId}
                                                                required={false}
                                                                onChange={(e) => set_responsivenessTypeId(e.target.value)}
                                                            >
                                                                <option value=''>Select...</option>
                                                                {EntityResponsivenessTypeOptions.map((r) => {
                                                                    return (
                                                                        <option
                                                                            key={r.id}
                                                                            value={r.id}
                                                                        >
                                                                            {r.name}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </select>
                                                        </div>
                                                        {entity &&
                                                            entity.requiresConcernReason &&
                                                            entity.concernTypeId != concernTypeId && (
                                                                <div className='columns small-12 large-12'>
                                                                    <label htmlFor='cncernReason'>
                                                                        Reason for Concern Change: <span className='req'>*</span>
                                                                    </label>
                                                                    <input
                                                                        type='text'
                                                                        value={concernReason}
                                                                        required={true}
                                                                        onChange={(e) => set_concernReason(e.target.value)}
                                                                    />
                                                                </div>
                                                            )}
                                                    </div>
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='workingRelationship'>Working Relationship:</label>
                                                            <input
                                                                type='text'
                                                                id='workingRelationship'
                                                                value={workingRelationship}
                                                                maxLength='100'
                                                                onChange={(e) => set_workingRelationship(e.target.value)}
                                                            />
                                                        </div>
                                                    </div>

                                                    <h2
                                                        className='decorated'
                                                        style={styles.sectionHeader}
                                                    >
                                                        Address
                                                    </h2>
                                                    <div className='row'>
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={styles.formColumn}
                                                        >
                                                            <input
                                                                type='checkbox'
                                                                value={addressSameAsCompany}
                                                                style={{
                                                                    marginRight: '5px',
                                                                }}
                                                                onChange={(e) => onSameAsCompanyAddressChanged(e.target.checked)}
                                                                checked={addressSameAsCompany}
                                                                disabled={!companyId}
                                                            ></input>
                                                            Same as Business (Marketing Name) Address
                                                        </div>
                                                        <div
                                                            className='columns small-12 large-6'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='street1'>
                                                                Street Address <span className='req'>*</span>
                                                            </label>
                                                            <input
                                                                id='street1'
                                                                type='text'
                                                                value={street1}
                                                                onChange={(event) => set_street1(event.target.value)}
                                                                required={true}
                                                                maxLength={50}
                                                                disabled={addressSameAsCompany}
                                                            />
                                                        </div>
                                                        <div
                                                            className='columns small-12 large-6'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='street2'>Street Address 2</label>
                                                            <input
                                                                id='street2'
                                                                type='text'
                                                                value={street2}
                                                                onChange={(event) => set_street2(event.target.value)}
                                                                maxLength={50}
                                                                disabled={addressSameAsCompany}
                                                            />
                                                        </div>
                                                        <div
                                                            className='columns small-12 large-6'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='city'>
                                                                City <span className='req'>*</span>
                                                            </label>
                                                            <input
                                                                id='city'
                                                                type='text'
                                                                value={city}
                                                                onChange={(event) => set_city(event.target.value)}
                                                                required={true}
                                                                maxLength={50}
                                                                disabled={addressSameAsCompany}
                                                            />
                                                        </div>
                                                        <div
                                                            className='columns small-12 medium-6 large-3'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='state'>
                                                                State <span className='req'>*</span>
                                                            </label>
                                                            <StateSelect
                                                                id='state'
                                                                value={state}
                                                                onChange={(event) => set_state(event.target.value)}
                                                                required={true}
                                                                disabled={addressSameAsCompany}
                                                            />
                                                        </div>
                                                        <div
                                                            className='columns small-12 medium-6 large-3'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='zipCode'>
                                                                Zip Code <span className='req'>*</span>
                                                            </label>
                                                            <ZipCodeInput
                                                                id='zipCode'
                                                                value={zipCode}
                                                                onChange={(event) => set_zipcode(event.target.value)}
                                                                required={true}
                                                                maxLength={10}
                                                                disabled={addressSameAsCompany}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </Item>
                                            <Item
                                                title={tabNames[1]}
                                                visible={entityId}
                                            >
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        minHeight: '700px',
                                                    }}
                                                >
                                                    <EntityPrincipalLinks
                                                        entityId={selectedTabIndex === 1 ? entityId : null}
                                                        parentId='editEntityModal'
                                                    />
                                                </div>
                                            </Item>
                                            <Item
                                                title={tabNames[2]}
                                                visible={entityId}
                                            >
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        minHeight: '700px',
                                                    }}
                                                >
                                                    <EntityApplicationsTable
                                                        entityId={selectedTabIndex === 2 ? entityId : null}
                                                        gridStore={applicationsStore}
                                                    />
                                                </div>
                                            </Item>
                                            <Item
                                                title={tabNames[3]}
                                                visible={entityId}
                                            >
                                                <div style={styles.loansParentPanel}>
                                                    <h2
                                                        className='decorated'
                                                        style={styles.sectionHeader}
                                                    >
                                                        Not Closed
                                                    </h2>
                                                    <EntityLoansTable
                                                        entityId={selectedTabIndex === 3 ? entityId : null}
                                                        masterNotes={true}
                                                        gridStore={masterNotesStore}
                                                        isClosed={false}
                                                    />
                                                    <h2
                                                        className='decorated'
                                                        style={styles.sectionHeader}
                                                    >
                                                        Closed
                                                    </h2>
                                                    <EntityLoansTable
                                                        entityId={selectedTabIndex === 3 ? entityId : null}
                                                        masterNotes={true}
                                                        gridStore={masterNotesStore}
                                                        isClosed={true}
                                                    />
                                                </div>
                                            </Item>
                                            <Item
                                                title={tabNames[4]}
                                                visible={entityId}
                                            >
                                                <div style={styles.loansParentPanel}>
                                                    <h2
                                                        className='decorated'
                                                        style={styles.sectionHeader}
                                                    >
                                                        Not Closed
                                                    </h2>
                                                    <EntityLoansTable
                                                        entityId={selectedTabIndex === 4 ? entityId : null}
                                                        masterNotes={false}
                                                        gridStore={loansStore}
                                                        isClosed={false}
                                                    />
                                                    <h2
                                                        className='decorated'
                                                        style={styles.sectionHeader}
                                                    >
                                                        Closed
                                                    </h2>
                                                    <EntityLoansTable
                                                        entityId={selectedTabIndex === 4 ? entityId : null}
                                                        masterNotes={false}
                                                        gridStore={loansStore}
                                                        isClosed={true}
                                                    />
                                                </div>
                                            </Item>
                                        </TabPanel>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='submit'
                                            className='button primary'
                                            title='Save changes and exit'
                                            disabled={selectedTabIndex > 0}
                                        >
                                            Save & Exit
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={handleClose}
                                            title='Abandon changes and exit'
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <ValidationMessagesModal
                open={!!validation}
                validations={validation}
                handleConfirm={onValidationConfirm}
                handleCancel={onValidationCancel}
            />
        </>
    );
};

export default EditEntityModal;
